import React, {
    useEffect,
    useMemo,
    useState,
} from 'react'

import {
    Button,
    Card,
    Col,
    Divider,
    Form,
    Image,
    Input,
    InputNumber,
    message,
    Modal,
    Row,
    Select,
    Space,
    Switch,
    Table,
    Tag,
    Upload,
} from 'antd'

import {
    UploadOutlined,
} from '@ant-design/icons'

import axios from 'axios'
import UploadService from 'services/UploadService'

import { env } from 'configs/EnvironmentConfig'
import { AUTH_TOKEN } from 'constants/AuthConstant'

const { Option } = Select

const BalloonSettings = () => {
    const [form] = Form.useForm()
    const [itemForm] = Form.useForm()

    const [games, setGames] =
        useState([])

    const [
        selectedGameId,
        setSelectedGameId,
    ] = useState(null)

    const [
        loadingGames,
        setLoadingGames,
    ] = useState(false)

    const [
        loadingConfig,
        setLoadingConfig,
    ] = useState(false)

    const [
        savingConfig,
        setSavingConfig,
    ] = useState(false)

    const [
        config,
        setConfig,
    ] = useState(null)

    const [
        items,
        setItems,
    ] = useState([])

    const [
        itemModalOpen,
        setItemModalOpen,
    ] = useState(false)

    const [
        editingItem,
        setEditingItem,
    ] = useState(null)

    const [
        savingItem,
        setSavingItem,
    ] = useState(false)

    const [
        uploadingItemImage,
        setUploadingItemImage,
    ] = useState(false)

    const [
        itemImageUrl,
        setItemImageUrl,
    ] = useState(null)

    const selectedGame =
        useMemo(() => {
            return games.find(
                (game) =>
                    game.id ===
                    selectedGameId,
            )
        }, [
            games,
            selectedGameId,
        ])

    // =========================================================
    // API
    // =========================================================

    const getHeaders = () => {
        const token =
            localStorage.getItem(
                AUTH_TOKEN,
            ) ||
            localStorage.getItem(
                'AUTH_TOKEN',
            ) ||
            localStorage.getItem(
                'ACCESS_TOKEN',
            )

        const clientId =
            localStorage.getItem(
                'CLIENT_ID',
            )

        return {
            ...(token
                ? {
                    Authorization:
                        `Bearer ${token}`,
                }
                : {}),

            ...(clientId
                ? {
                    'x-client-id':
                        clientId,
                }
                : {}),
        }
    }

    // =========================================================
    // CARREGAR JOGOS
    // =========================================================

    const loadGames =
        async () => {
            try {
                setLoadingGames(
                    true,
                )

                const response =
                    await axios.get(
                        `${env.API_ENDPOINT_URL}/games`,
                        {
                            headers:
                                getHeaders(),
                        },
                    )

                const raw =
                    Array.isArray(
                        response.data,
                    )
                        ? response.data
                        : response.data
                            ?.data || []

                const balloonGames =
                    raw.filter(
                        (game) =>
                            game.type ===
                            'BALLOON_POP',
                    )

                setGames(
                    balloonGames,
                )

                if (
                    !selectedGameId &&
                    balloonGames.length >
                    0
                ) {
                    setSelectedGameId(
                        balloonGames[0]
                            .id,
                    )
                }
            } catch (error) {
                console.error(
                    error,
                )

                message.error(
                    'Não foi possível carregar os jogos de balão.',
                )
            } finally {
                setLoadingGames(
                    false,
                )
            }
        }

    // =========================================================
    // CARREGAR CONFIGURAÇÃO
    // =========================================================

    const loadConfig =
        async (gameId) => {
            if (!gameId) {
                return
            }

            try {
                setLoadingConfig(
                    true,
                )

                const response =
                    await axios.get(
                        `${env.API_ENDPOINT_URL}/balloon-games/${gameId}`,
                        {
                            headers:
                                getHeaders(),
                        },
                    )

                const balloonConfig =
                    response.data
                        ?.config

                setConfig(
                    balloonConfig,
                )

                setItems(
                    balloonConfig
                        ?.items || [],
                )

                form.setFieldsValue(
                    balloonConfig || {},
                )
            } catch (error) {
                console.error(
                    error,
                )

                message.error(
                    'Não foi possível carregar a configuração.',
                )
            } finally {
                setLoadingConfig(
                    false,
                )
            }
        }

    useEffect(() => {
        loadGames()
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        if (
            selectedGameId
        ) {
            loadConfig(
                selectedGameId,
            )
        }
        // eslint-disable-next-line
    }, [
        selectedGameId,
    ])

    // =========================================================
    // SALVAR CONFIGURAÇÃO
    // =========================================================

    const handleSaveConfig =
        async () => {
            if (
                !selectedGameId
            ) {
                return
            }

            try {
                const values =
                    await form.validateFields()

                setSavingConfig(
                    true,
                )

                await axios.put(
                    `${env.API_ENDPOINT_URL}/balloon-games/${selectedGameId}/config`,
                    values,
                    {
                        headers:
                            getHeaders(),
                    },
                )

                message.success(
                    'Configuração salva com sucesso.',
                )

                await loadConfig(
                    selectedGameId,
                )
            } catch (error) {
                console.error(
                    error,
                )

                message.error(
                    'Não foi possível salvar a configuração.',
                )
            } finally {
                setSavingConfig(
                    false,
                )
            }
        }

    // =========================================================
    // NOVO ITEM
    // =========================================================

    const handleNewItem =
        () => {
            setEditingItem(
                null,
            )
            setItemImageUrl(
                null,
            )

            itemForm.resetFields()

            itemForm.setFieldsValue(
                {
                    name:
                        'Novo balão',

                    type:
                        'NORMAL',

                    color:
                        '#1677ff',

                    points:
                        100,

                    probabilityWeight:
                        10,

                    speedMultiplier:
                        1,

                    size:
                        120,

                    isActive:
                        true,
                },
            )

            setItemModalOpen(
                true,
            )
        }

    // =========================================================
    // EDITAR ITEM
    // =========================================================

    const handleEditItem =
        (item) => {
            setEditingItem(
                item,
            )
            setItemImageUrl(
                item.imageUrl || null,
            )

            itemForm.setFieldsValue(
                item,
            )

            setItemModalOpen(
                true,
            )
        }

    const handleUploadItemImage =
        async (file) => {
            try {
                setUploadingItemImage(
                    true,
                )

                const result =
                    await UploadService.image(
                        file,
                    )

                const url =
                    result?.url ||
                    result?.data?.url ||
                    result

                if (!url) {
                    throw new Error(
                        'URL não retornada pelo upload.',
                    )
                }

                setItemImageUrl(
                    url,
                )

                itemForm.setFieldsValue({
                    imageUrl: url,
                })

                message.success(
                    'Imagem enviada.',
                )
            } catch (error) {
                console.error(
                    'ERRO UPLOAD ITEM:',
                    error,
                )

                message.error(
                    'Não foi possível enviar a imagem.',
                )
            } finally {
                setUploadingItemImage(
                    false,
                )
            }

            return false
        }

    // =========================================================
    // SALVAR ITEM
    // =========================================================

    const handleSaveItem =
        async () => {
            try {
                const values =
                    await itemForm.validateFields()

                setSavingItem(
                    true,
                )

                if (
                    editingItem
                ) {
                    await axios.put(
                        `${env.API_ENDPOINT_URL}/balloon-games/${selectedGameId}/items/${editingItem.id}`,
                        values,
                        {
                            headers:
                                getHeaders(),
                        },
                    )

                    message.success(
                        'Item atualizado.',
                    )
                } else {
                    await axios.post(
                        `${env.API_ENDPOINT_URL}/balloon-games/${selectedGameId}/items`,
                        values,
                        {
                            headers:
                                getHeaders(),
                        },
                    )

                    message.success(
                        'Item criado.',
                    )
                }

                setItemModalOpen(
                    false,
                )

                await loadConfig(
                    selectedGameId,
                )
            } catch (error) {
                console.error(
                    error,
                )

                message.error(
                    'Não foi possível salvar o item.',
                )
            } finally {
                setSavingItem(
                    false,
                )
            }
        }

    // =========================================================
    // EXCLUIR ITEM
    // =========================================================

    const handleDeleteItem =
        (item) => {
            Modal.confirm({
                title:
                    'Excluir item?',

                content:
                    `Deseja excluir "${item.name}"?`,

                okText:
                    'Excluir',

                cancelText:
                    'Cancelar',

                okType:
                    'danger',

                onOk:
                    async () => {
                        try {
                            await axios.delete(
                                `${env.API_ENDPOINT_URL}/balloon-games/${selectedGameId}/items/${item.id}`,
                                {
                                    headers:
                                        getHeaders(),
                                },
                            )

                            message.success(
                                'Item removido.',
                            )

                            await loadConfig(
                                selectedGameId,
                            )
                        } catch (
                        error
                        ) {
                            console.error(
                                error,
                            )

                            message.error(
                                'Não foi possível excluir o item.',
                            )
                        }
                    },
            })
        }

    const ITEM_TYPE_LABELS = {
        NORMAL: 'Balão normal',
        BONUS: 'Bônus',
        BOMB: 'Bomba',
        TIME: 'Tempo extra',
        MULTIPLIER: 'Multiplicador',
    }

    // =========================================================
    // COLUNAS
    // =========================================================

    const columns = [
        {
            title: 'Imagem',
            dataIndex: 'imageUrl',
            key: 'imageUrl',
            width: 90,

            render: (value, item) =>
                value ? (
                    <Image
                        src={value}
                        width={54}
                        height={54}
                        style={{
                            objectFit: 'contain',
                        }}
                        preview
                    />
                ) : (
                    <div
                        style={{
                            width: 54,
                            height: 54,
                            borderRadius: '50%',
                            background:
                                item.color ||
                                '#1677ff',
                        }}
                    />
                ),
        },

        {
            title: 'Nome',
            dataIndex: 'name',
            key: 'name',
        },

        {
            title: 'Tipo',
            dataIndex: 'type',
            key: 'type',

            render: (value) => (
                <Tag>
                    {ITEM_TYPE_LABELS[value] ||
                        value}
                </Tag>
            ),
        },

        {
            title: 'Pontos',
            dataIndex: 'points',
            key: 'points',
        },

        {
            title: 'Peso',
            dataIndex: 'probabilityWeight',
            key: 'probabilityWeight',
        },

        {
            title: 'Velocidade',
            dataIndex: 'speedMultiplier',
            key: 'speedMultiplier',

            render: (value) =>
                `${value}x`,
        },

        {
            title: 'Tamanho',
            dataIndex: 'size',
            key: 'size',

            render: (value) =>
                `${value}px`,
        },

        {
            title: 'Status',
            dataIndex: 'isActive',
            key: 'isActive',

            render: (value) =>
                value ? (
                    <Tag color="green">
                        Ativo
                    </Tag>
                ) : (
                    <Tag>
                        Inativo
                    </Tag>
                ),
        },

        {
            title: 'Ações',
            key: 'actions',

            render: (_, item) => (
                <Space>
                    <Button
                        size="small"
                        onClick={() =>
                            handleEditItem(
                                item,
                            )
                        }
                    >
                        Editar
                    </Button>

                    <Button
                        danger
                        size="small"
                        onClick={() =>
                            handleDeleteItem(
                                item,
                            )
                        }
                    >
                        Excluir
                    </Button>
                </Space>
            ),
        },
    ]

    // =========================================================
    // RENDER
    // =========================================================

    return (
        <div>
            <Card
                title="Jogo dos Balões"
                style={{
                    marginBottom:
                        24,
                }}
            >
                <Form.Item
                    label="Jogo"
                >
                    <Select
                        loading={
                            loadingGames
                        }
                        value={
                            selectedGameId
                        }
                        placeholder="Selecione o jogo"
                        style={{
                            width: 400,
                            maxWidth:
                                '100%',
                        }}
                        onChange={
                            setSelectedGameId
                        }
                    >
                        {games.map(
                            (game) => (
                                <Option
                                    key={
                                        game.id
                                    }
                                    value={
                                        game.id
                                    }
                                >
                                    {game.name}
                                </Option>
                            ),
                        )}
                    </Select>
                </Form.Item>

                {selectedGame && (
                    <div>
                        <strong>
                            {
                                selectedGame.name
                            }
                        </strong>

                        {' — '}

                        {
                            selectedGame.status
                        }
                    </div>
                )}
            </Card>

            {selectedGameId && (
                <>
                    <Card
                        title="Jogabilidade"
                        loading={
                            loadingConfig
                        }
                        style={{
                            marginBottom:
                                24,
                        }}
                    >
                        <Form
                            form={form}
                            layout="vertical"
                        >
                            <Row
                                gutter={16}
                            >
                                <Col
                                    xs={24}
                                    md={8}
                                >
                                    <Form.Item
                                        name="timeLimit"
                                        label="Tempo de jogo (segundos)"
                                    >
                                        <InputNumber
                                            min={5}
                                            max={300}
                                            style={{
                                                width:
                                                    '100%',
                                            }}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={8}
                                >
                                    <Form.Item
                                        name="spawnInterval"
                                        label="Intervalo entre balões (ms)"
                                    >
                                        <InputNumber
                                            min={100}
                                            max={5000}
                                            step={50}
                                            style={{
                                                width:
                                                    '100%',
                                            }}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={8}
                                >
                                    <Form.Item
                                        name="maxBalloonsOnScreen"
                                        label="Máximo na tela"
                                    >
                                        <InputNumber
                                            min={1}
                                            max={50}
                                            style={{
                                                width:
                                                    '100%',
                                            }}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={8}
                                >
                                    <Form.Item
                                        name="initialSpeed"
                                        label="Velocidade inicial"
                                    >
                                        <InputNumber
                                            min={0.1}
                                            max={10}
                                            step={0.1}
                                            style={{
                                                width:
                                                    '100%',
                                            }}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={8}
                                >
                                    <Form.Item
                                        name="maxSpeed"
                                        label="Velocidade máxima"
                                    >
                                        <InputNumber
                                            min={0.1}
                                            max={20}
                                            step={0.1}
                                            style={{
                                                width:
                                                    '100%',
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Divider />

                            <Space
                                wrap
                                size="large"
                            >
                                <Form.Item
                                    name="progressiveDifficulty"
                                    label="Dificuldade progressiva"
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Form.Item>

                                <Form.Item
                                    name="showTimer"
                                    label="Mostrar tempo"
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Form.Item>

                                <Form.Item
                                    name="showScore"
                                    label="Mostrar pontos"
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Form.Item>

                                <Form.Item
                                    name="showCombo"
                                    label="Mostrar combo"
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Form.Item>

                                <Form.Item
                                    name="comboEnabled"
                                    label="Ativar combo"
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Form.Item>

                                <Form.Item
                                    name="showRanking"
                                    label="Mostrar ranking"
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Form.Item>

                                <Form.Item
                                    name="frenzyEnabled"
                                    label="Ativar frenesi"
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Form.Item>
                            </Space>

                            <Divider />

                            <Row
                                gutter={16}
                            >
                                <Col
                                    xs={24}
                                    md={8}
                                >
                                    <Form.Item
                                        name="frenzyStartSeconds"
                                        label="Frenesi nos últimos (s)"
                                    >
                                        <InputNumber
                                            min={1}
                                            max={30}
                                            style={{
                                                width:
                                                    '100%',
                                            }}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={8}
                                >
                                    <Form.Item
                                        name="frenzySpawnMultiplier"
                                        label="Multiplicador de aparição"
                                    >
                                        <InputNumber
                                            min={1}
                                            max={10}
                                            step={0.1}
                                            style={{
                                                width:
                                                    '100%',
                                            }}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={8}
                                >
                                    <Form.Item
                                        name="frenzySpeedMultiplier"
                                        label="Multiplicador de velocidade"
                                    >
                                        <InputNumber
                                            min={1}
                                            max={10}
                                            step={0.1}
                                            style={{
                                                width:
                                                    '100%',
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Button
                                type="primary"
                                loading={
                                    savingConfig
                                }
                                onClick={
                                    handleSaveConfig
                                }
                            >
                                Salvar configurações
                            </Button>
                        </Form>
                    </Card>

                    <Card
                        title="Balões e itens"
                        extra={
                            <Button
                                type="primary"
                                onClick={
                                    handleNewItem
                                }
                            >
                                Novo item
                            </Button>
                        }
                    >
                        <Table
                            rowKey="id"
                            columns={
                                columns
                            }
                            dataSource={
                                items
                            }
                            pagination={
                                false
                            }
                        />
                    </Card>
                </>
            )}

            <Modal
                title={
                    editingItem
                        ? 'Editar item'
                        : 'Novo item'
                }
                open={
                    itemModalOpen
                }
                onCancel={() =>
                    setItemModalOpen(
                        false,
                    )
                }
                onOk={
                    handleSaveItem
                }
                confirmLoading={
                    savingItem
                }
                okText="Salvar"
                cancelText="Cancelar"
                destroyOnClose
            >
                <Form
                    form={
                        itemForm
                    }
                    layout="vertical"
                >
                    <Form.Item
                        name="name"
                        label="Nome"
                        rules={[
                            {
                                required:
                                    true,
                                message:
                                    'Informe o nome.',
                            },
                        ]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name="type"
                        label="Tipo"
                        rules={[
                            {
                                required:
                                    true,
                            },
                        ]}
                    >
                        <Select>
                            <Option value="NORMAL">
                                Normal
                            </Option>

                            <Option value="BONUS">
                                Bônus
                            </Option>

                            <Option value="BOMB">
                                Bomba
                            </Option>

                            <Option value="TIME">
                                Tempo
                            </Option>

                            <Option value="MULTIPLIER">
                                Multiplicador
                            </Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Imagem do item"
                    >
                        <Space
                            direction="vertical"
                            style={{
                                width: '100%',
                            }}
                        >
                            <Upload
                                accept="image/*"
                                showUploadList={false}
                                beforeUpload={
                                    handleUploadItemImage
                                }
                            >
                                <Button
                                    icon={
                                        <UploadOutlined />
                                    }
                                    loading={
                                        uploadingItemImage
                                    }
                                >
                                    Enviar imagem
                                </Button>
                            </Upload>

                            {itemImageUrl && (
                                <div>
                                    <Image
                                        src={
                                            itemImageUrl
                                        }
                                        width={120}
                                        height={120}
                                        style={{
                                            objectFit:
                                                'contain',
                                            borderRadius:
                                                12,
                                        }}
                                        preview
                                    />

                                    <div
                                        style={{
                                            marginTop: 8,
                                        }}
                                    >
                                        <Button
                                            danger
                                            size="small"
                                            onClick={() => {
                                                setItemImageUrl(
                                                    null,
                                                )

                                                itemForm.setFieldsValue({
                                                    imageUrl:
                                                        null,
                                                })
                                            }}
                                        >
                                            Remover imagem
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </Space>
                    </Form.Item>

                    <Form.Item
                        name="imageUrl"
                        hidden
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name="color"
                        label="Cor"
                    >
                        <Input />
                    </Form.Item>

                    <Row
                        gutter={16}
                    >
                        <Col
                            span={12}
                        >
                            <Form.Item
                                name="points"
                                label="Pontos"
                            >
                                <InputNumber
                                    style={{
                                        width:
                                            '100%',
                                    }}
                                />
                            </Form.Item>
                        </Col>

                        <Col
                            span={12}
                        >
                            <Form.Item
                                name="probabilityWeight"
                                label="Peso de aparição"
                            >
                                <InputNumber
                                    min={0}
                                    style={{
                                        width:
                                            '100%',
                                    }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row
                        gutter={16}
                    >
                        <Col
                            span={12}
                        >
                            <Form.Item
                                name="speedMultiplier"
                                label="Velocidade"
                            >
                                <InputNumber
                                    min={0.1}
                                    step={0.1}
                                    style={{
                                        width:
                                            '100%',
                                    }}
                                />
                            </Form.Item>
                        </Col>

                        <Col
                            span={12}
                        >
                            <Form.Item
                                name="size"
                                label="Tamanho (px)"
                            >
                                <InputNumber
                                    min={20}
                                    style={{
                                        width:
                                            '100%',
                                    }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="isActive"
                        label="Ativo"
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    )
}

export default BalloonSettings